import React, { useState, useEffect } from 'react';
import { Badge, Button, Modal } from 'react-bootstrap';
import { FaCloud, FaDatabase, FaBolt, FaServer, FaTable, FaUserShield, FaNetworkWired, FaProjectDiagram, FaShieldAlt, FaGlobe, FaRoute, FaPlug } from 'react-icons/fa';
import 'bootstrap/dist/css/bootstrap.min.css';

const ResourceDetailsTableEnhanced = ({ resources = [] }) => {
  const [selectedResource, setSelectedResource] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    setCurrentPage(1);
  }, [resources]);

  const getResourceIcon = (type) => {
    const icons = {
      's3-buckets': <FaDatabase />,
      'iam-roles': <FaUserShield />,
      'lambda-functions': <FaBolt />,
      'ec2-instances': <FaServer />,
      'dynamodb-tables': <FaTable />,
      'vpcs': <FaNetworkWired />,
      'subnets': <FaProjectDiagram />,
      'security-groups': <FaShieldAlt />,
      'cloudfront-distributions': <FaGlobe />,
      'cloudtrail-trails': <FaRoute />,
      'internet-gateways': <FaNetworkWired />,
      'api-gateways': <FaPlug />,
      'rds-instances': <FaDatabase />,
    };
    return icons[type] || <FaCloud />;
  };

  const getCategoryBadge = (type) => {
    const categoryMap = {
      's3-buckets': { category: 'Storage', color: '#10b981' },
      'lambda-functions': { category: 'Serverless', color: '#3b82f6' },
      'ec2-instances': { category: 'Compute', color: '#f59e0b' },
      'dynamodb-tables': { category: 'Database', color: '#8b5cf6' },
      'iam-roles': { category: 'Security', color: '#ef4444' },
      'vpcs': { category: 'Network', color: '#06b6d4' },
      'subnets': { category: 'Network', color: '#06b6d4' },
      'security-groups': { category: 'Security', color: '#ef4444' },
      'cloudfront-distributions': { category: 'CDN', color: '#ec4899' },
      'cloudtrail-trails': { category: 'Monitoring', color: '#6366f1' },
      'internet-gateways': { category: 'Network', color: '#06b6d4' },
      'api-gateways': { category: 'API', color: '#6366f1' },
    };
    return categoryMap[type] || { category: 'Other', color: '#6b7280' };
  };

  if (!resources || !Array.isArray(resources) || resources.length === 0) {
    return (
      <div style={{ 
        background: 'white', 
        borderRadius: '12px', 
        border: '1px solid #e9ecef',
        padding: '40px',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '3rem', color: '#e5e7eb', marginBottom: '16px' }}>
          <FaCloud />
        </div>
        <h5 style={{ color: '#6b7280', marginBottom: '8px' }}>No resources found</h5>
        <p className="text-muted">Try adjusting your filters or search query</p>
      </div>
    );
  }

  const handleViewDetails = (resource) => {
    setSelectedResource(resource);
    setShowModal(true);
  };

  const totalPages = Math.max(1, Math.ceil(resources.length / itemsPerPage));
  const currentData = resources.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(prev => prev + 1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(prev => prev - 1);
  };

  const parseMetadata = (resource) => {
    try {
      if (resource.metadata?.resourceData) {
        return JSON.parse(resource.metadata.resourceData);
      }
      return {};
    } catch {
      return {};
    }
  };

  return (
    <>
      <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e9ecef', overflow: 'hidden' }}>
        <div style={{ 
          background: '#6D5BFF', 
          color: 'white', 
          padding: '16px 20px', 
          fontSize: '1.1rem',
          fontWeight: '600'
        }}>
          Cloud Resources
        </div>
        
        <div>
          {currentData.map((resource, idx) => {
            const badge = getCategoryBadge(resource.type);
            return (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '16px 20px',
                  borderBottom: idx < currentData.length - 1 ? '1px solid #e9ecef' : 'none',
                  transition: 'all 0.2s',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <div className="d-flex align-items-center" style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '8px',
                    background: 'rgba(109, 91, 255, 0.1)',
                    color: '#6D5BFF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.25rem',
                    marginRight: '16px',
                    flexShrink: 0
                  }}>
                    {getResourceIcon(resource.type)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ 
                      fontWeight: '600', 
                      color: '#1e293b',
                      marginBottom: '4px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {resource.name || resource.id}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                      {resource.type} • {resource.region || 'Global'}
                    </div>
                  </div>
                </div>
                
                <div className="d-flex align-items-center gap-2">
                  <div
                    style={{
                      padding: '4px 12px',
                      borderRadius: '6px',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      color: 'white',
                      backgroundColor: badge.color
                    }}
                  >
                    {badge.category}
                  </div>
                  <Button
                    size="sm"
                    style={{
                      backgroundColor: '#6D5BFF',
                      border: 'none',
                      fontWeight: '600',
                      padding: '6px 16px'
                    }}
                    onClick={() => handleViewDetails(resource)}
                  >
                    View
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
        
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          borderTop: '1px solid #e9ecef',
          background: '#f8f9fa'
        }}>
          <Button
            size="sm"
            variant={currentPage > 1 ? 'primary' : 'secondary'}
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
            style={currentPage > 1 ? { backgroundColor: '#6D5BFF', border: 'none' } : {}}
          >
            Previous
          </Button>
          <small style={{ fontWeight: '600', color: '#64748b' }}>
            Page {currentPage} of {totalPages}
          </small>
          <Button
            size="sm"
            variant={currentPage < totalPages ? 'primary' : 'secondary'}
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            style={currentPage < totalPages ? { backgroundColor: '#6D5BFF', border: 'none' } : {}}
          >
            Next
          </Button>
        </div>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Resource Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedResource && (
            <div>
              <h6 className="fw-bold">Basic Information</h6>
              <div className="border rounded p-3 mb-3">
                <div className="row mb-2">
                  <div className="col-4"><strong>Name</strong></div>
                  <div className="col-8">{selectedResource.name || selectedResource.id}</div>
                </div>
                <div className="row mb-2">
                  <div className="col-4"><strong>Type</strong></div>
                  <div className="col-8">{selectedResource.type}</div>
                </div>
                <div className="row mb-2">
                  <div className="col-4"><strong>Region</strong></div>
                  <div className="col-8">{selectedResource.region || 'Global'}</div>
                </div>
                <div className="row mb-2">
                  <div className="col-4"><strong>Account ID</strong></div>
                  <div className="col-8">{selectedResource.metadata?.accountId || 'N/A'}</div>
                </div>
                {selectedResource.arn && (
                  <div className="row">
                    <div className="col-4"><strong>ARN</strong></div>
                    <div className="col-8 text-break">{selectedResource.arn}</div>
                  </div>
                )}
              </div>

              <h6 className="fw-bold mt-3">Resource Data</h6>
              <pre className="bg-light p-3 rounded" style={{ maxHeight: '300px', overflow: 'auto' }}>
                {JSON.stringify(parseMetadata(selectedResource), null, 2)}
              </pre>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ResourceDetailsTableEnhanced;
